import { Drawer, List } from 'antd';
import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import { condenseTemplatePhasesAndCostcodes } from '../helpers/costcodeHelpers';
import useProjectGlobalCostcodes from '../costcodes/ProjectGlobalCostcodes';

export default function ProjectAddCostCodeTemplate({
  divisionId,
  hasCostcodes,
  applyTemplate,
}) {
  const { t } = useTranslation();
  const templates = useSelector((state) => state.costcodes.templates);
  const [showDrawer, setShowDrawer] = useState(false);

  const { globalCostcodes } = useProjectGlobalCostcodes(showDrawer);

  const ourTemplates = useMemo(() => (templates
    ? templates.filter((t) => t.divisionId === divisionId)
    : []
  ), [divisionId, templates]);

  const globalCodes = useMemo(() => (
    new Set(globalCostcodes.map((cc) => cc.id))
  ), [globalCostcodes]);

  const onAddTemplate = useCallback(() => {
    setShowDrawer(true);
  }, []);

  const onClose = useCallback(() => {
    setShowDrawer(false);
  });

  const onSelectTemplate = useCallback((template) => () => {
    // Format data and send to ProjectAddView
    const {
      data: {
        phases: selectedPhases = [],
        costcodes: selectedCostcodes = [],
        phaseCostCodes: selectedPhaseCostcodes = [],
      },
    } = template;
    const condensedPhases = condenseTemplatePhasesAndCostcodes(
      selectedPhases,
      selectedPhaseCostcodes,
      selectedCostcodes,
    );

    const formattedCostcodes = selectedCostcodes.map((costcode) => {
      const {
        categoryIds,
        categoryId,
        categoryAssignments,
      } = costcode;

      let categoryAssignmentsToUse = categoryAssignments;
      if (!categoryAssignmentsToUse) {
        categoryAssignmentsToUse = {
          [categoryId]: {
            defaultCategoryId: categoryId,
            hours: costcode.hours,
            days: costcode.days,
            estimatedCost: costcode.estimatedCost,
            estimatedQuantity: costcode.estimatedQuantity,
            units: costcode.units,
            phaseId: costcode.phaseId,
            costcodeId: costcode.id,
          },
        };
      }
      return {
        ...costcode,
        categoryAssignments: categoryAssignmentsToUse,
        categoryIds: categoryIds ?? [categoryId],
        isNew: !globalCodes.has(costcode.id),
      };
    });

    applyTemplate({
      costcodes: formattedCostcodes,
      phases: condensedPhases,
    });
    setShowDrawer(false);
  }, [applyTemplate, globalCodes]);

  const renderTemplate = useCallback((item) => (
    <List.Item
      key={item.id}
      extra={(
        <div style={{ height: '100%' }}>
          <OnTraccrButton
            title="Apply"
            onClick={onSelectTemplate(item)}
          />
        </div>
      )}
    >
      <List.Item.Meta
        title={item.name}
      />
      <div>{item.description}</div>
    </List.Item>
  ), [onSelectTemplate]);

  return (
    <>
      <OnTraccrButton
        title="Apply Costing Template"
        onClick={onAddTemplate}
        disabled={hasCostcodes}
        style={{ marginTop: 5 }}
      />
      <Drawer
        title={`Apply Costcode Template to ${t('Project')}`}
        visible={showDrawer}
        onClose={onClose}
        width={400}
      >
        <List
          dataSource={ourTemplates}
          itemLayout="vertical"
          locale={{ emptyText: 'No templates found for this division' }}
          renderItem={renderTemplate}
        />
      </Drawer>
    </>
  );
}
